import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  WatermarkStage,
  WMAbnormal,
  WMAtm,
  WMLoan,
  WMParking,
  WMProfit,
} from "../components/styled/WaterMark";
import { useLocalization } from "../hooks/useLocalization";
import { imageCache } from "../utils/imageCache";

/** ====== 外層容器：用 flex 垂直排版 ====== */
const Page = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-width: 1024px;
  margin: 0 auto;
  z-index: 10; /* 蓋在 layout 背景上 */
`;

/** ====== 上方文案區（副標、主標、CTA） ====== */
const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  margin-top: 1.5rem;
  z-index: 20; /* 高於底部所有浮水印與圖片 */
`;

const SubTitle = styled.div`
  font-family: "PingFang TC";
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  /* identical to box height, or 408% */
  text-align: center;
  letter-spacing: 3px;

  background: linear-gradient(90deg, #cbcede -8.1%, #646c8a 108.63%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;

  z-index: 20; /* 高於蒙塵 */

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 24px;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-family: "PingFang TC";
  font-style: normal;
  font-weight: 600;
  font-size: 42px;
  /* identical to box height, or 117% */
  text-align: center;
  letter-spacing: 0.02em;

  background: linear-gradient(
    90deg,
    #757ea7 -8.1%,
    #ffffff 53.63%,
    #3e466d 108.63%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;

  z-index: 20; /* 高於蒙塵 */

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 80px;
  }
`;

const CtaBtn = styled.button`
  margin-top: 8px;
  padding: 14px 40px;
  box-sizing: border-box;

  /* 使用 flexbox 實現完美垂直居中 */
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px; /* 改用 min-height 確保最小高度 */

  color: #ffffff;
  font-size: 24px;
  font-weight: 500;
  letter-spacing: 1px;
  line-height: 1.2; /* 設定適當的行高 */

  border: none;
  background: linear-gradient(360deg, #0c34f8 0%, #90b3ff 100%);
  box-shadow: 0px 0px 32.8px #134eff, 0px 4px 6.1px rgba(0, 0, 0, 0.25);
  border-radius: 110px;

  transition: filter 0.15s ease, transform 0.1s ease;
  &:hover {
    filter: brightness(1.07);
  }
  &:active {
    transform: scale(0.99);
  }
  cursor: pointer;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 32px;
    min-height: 80px;
    min-width: 240px;
  }
`;

/* ====== 響應式 SCAM 層（改為 fixed） ====== */
const ScamLayer = styled.div`
  --scam-scale: clamp(0.36, calc(100vw / 1024), 1);
  position: fixed;
  bottom: clamp(15%, 25vh, 35%); /* ✅ 從底部往上推，依螢幕高自動調整 */
  transform: translateX(-50%) scale(var(--scam-scale));
  transform-origin: center bottom;
  pointer-events: none;
  z-index: 9; /* ✅ 高於 WatermarkStage，低於 RobotWrap */
`;

/* ====== SCAM 字樣：依照 Figma 樣式比例縮放 ====== */
const ScamText = styled.div`
  font-family: "PingFang TC", "SF Pro TC", "Noto Sans TC", system-ui, sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 115px;
  line-height: 84px;
  letter-spacing: 5px;
  text-align: center;

  width: 100vw;
  height: 83px;

  background: linear-gradient(
    92.81deg,
    rgba(255, 255, 255, 0) -3.72%,
    #ffffff 99.11%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;

  opacity: 0.9;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 180px;
    line-height: 1;
    height: 180px;
  }
`;

/** ====== 底部主圖：最上層（貼底、從底下約 20% 被裁） ====== */
const RobotWrap = styled.div<{ $max?: number }>`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: env(safe-area-inset-bottom, 0px); /* 永遠貼齊螢幕底端 */
  width: clamp(
    300px,
    90vw,
    800px
  ); /* 響應式寬度：最小300px，理想90vw，最大800px */
  z-index: 26; /* 比背景和塵層高，低於 Header */
  pointer-events: none;
  max-height: 65vh;

  /* 針對手機尺寸 */
  @media (max-width: 767px) {
    width: clamp(280px, 95vw, 400px);
    max-height: 50vh;
  }

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    width: clamp(500px, 90vw, 850px);
    max-height: 70vh;
  }

  img {
    width: 100%;
    height: auto; /* 等比例縮放 */
    display: block;
    user-select: none;
    pointer-events: none;
  }
`;

const BottomImg = styled.img`
  height: 100%;
  width: auto;
  max-height: 60vh;
  max-width: 100%;
  object-fit: cover;
  user-select: none;
  pointer-events: none;
`;

const Animation = () => {
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [cachedUrls, setCachedUrls] = useState<string[]>([]);

  // 圖片路徑陣列
  const animationImages = useMemo(
    () =>
      Array.from(
        { length: 104 },
        (_, i) => `/animation/robot/main_animation${i + 5}.png`
      ),
    []
  );

  // 預載並快取所有圖片到記憶體
  useEffect(() => {
    let isMounted = true;

    imageCache
      .loadBatch(animationImages)
      .then((objectUrls) => {
        if (isMounted) {
          setCachedUrls(objectUrls);
          setIsLoaded(true);
        }
      })
      .catch(() => {
        if (isMounted) {
          setHasError(true);
        }
      });

    return () => {
      isMounted = false;
      // 組件卸載時清理快取（可選，根據需求決定）
      // imageCache.clear();
    };
  }, [animationImages]);

  // 動畫播放邏輯（使用 requestAnimationFrame 提升性能）
  useEffect(() => {
    if (!isLoaded || hasError) return;

    let animationFrameId: number;
    let lastTime = performance.now();
    const frameInterval = 40; // 40ms per frame (~25 FPS)

    const animate = (currentTime: number) => {
      const deltaTime = currentTime - lastTime;

      if (deltaTime >= frameInterval) {
        setCurrentFrame((prev) => (prev + 1) % cachedUrls.length); // 循環播放
        lastTime = currentTime - (deltaTime % frameInterval);
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isLoaded, hasError, currentFrame, cachedUrls.length]);

  // 載入中或錯誤狀態
  if (hasError || !isLoaded) {
    return (
      <BottomImg
        src="/animation/robot/main_animation5.png"
        alt="AI Scam Fighter"
      />
    );
  }

  return (
    <BottomImg
      src={cachedUrls[currentFrame]}
      alt={`AI Scam Fighter Animation Frame ${currentFrame + 1}`}
    />
  );
};

export default function HomePage() {
  const navigate = useNavigate();
  const { t } = useLocalization();

  return (
    <Page>
      {/* 響應式背景浮水印：整張舞台等比縮放 */}
      <WatermarkStage>
        <WMLoan>{t("home.loanAd")}</WMLoan>
        <WMParking>{t("home.parkingFee")}</WMParking>
        <WMProfit>{t("home.investmentAd")}</WMProfit>
        <WMAbnormal>{t("home.accountAbnormal")}</WMAbnormal>
        <WMAtm>{t("home.atmInstruction")}</WMAtm>
      </WatermarkStage>

      {/* SCAM 層（獨立、在浮水印上方） */}
      <ScamLayer>
        <ScamText>SCAM</ScamText>
      </ScamLayer>

      {/* 上方文案 */}
      <HeroCopy>
        <SubTitle>{t("scam_fighter")}</SubTitle>
        <Title>{t("ai_scam_guardian")}</Title>
        <CtaBtn onClick={() => navigate("/chatbot")}>
          {t("experience_now")}
        </CtaBtn>
      </HeroCopy>

      {/* 機器人圖（最高層） */}
      <RobotWrap $max={1}>
        <Animation />
      </RobotWrap>
    </Page>
  );
}
